import React from "react";
import { GenderValue, isPopulationFeature, Relations } from "shared";
import { FeatureTerm, featureToTerms, valueName } from "fe-common/logic";
import { QualifiedFeatureFragment } from "fe-common/graphql";
import styled from "@mui/material/styles/styled";
import { KahunCategories } from "shared";

const NegateText = styled("span")({
  fontWeight: 600,
});

const FeatureText = styled("span")({});

const BloodText = styled("span")({
  color: "grey",
});

const ResultText = styled("span")(({ positive }: { positive?: string }) => ({
  color: positive === "true" ? "red" : "#026957",
}));

const ValueText = styled("span")(({ value }: { value?: string }) => ({
  color:
    value === "High" || value === "Low"
      ? "red"
      : value === "Normal"
      ? "#026957"
      : "inherit",
}));

export function findingFormat(
  qf: QualifiedFeatureFragment,
  skipFeature = false,
  relationsToIgnore?: string[],
  featuresToOmitName?: string[],
) {
  const featureToTermsValue = featureToTerms(qf, relationsToIgnore).slice(0, 1);

  return featureToTermsValue.map((term, i) =>
    termToElements(
      i === 0 && skipFeature ? { ...term, feature: null } : term,
      relationsToIgnore,
      featuresToOmitName,
    ),
  );
}

function mainName(e: { name: string; alias?: string; shortname?: string }) {
  const name = e ? e.name || e.alias : null;

  if (e?.name === "Drug") {
    return "Medications";
  }

  return name;
}

export function termToElements(
  t: FeatureTerm,
  relationsToIgnore: string[] = [],
  featuresToOmit: string[] = [],
) {
  const { feature, negate, value } = t;
  const featureStr = mainName(feature) || "";
  const valueStr = valueName(value) || "";
  const negation = negate ? "No " : "";

  if (
    value?.relation?.id === Relations.LEVEL &&
    !relationsToIgnore.includes(Relations.LEVEL)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <NegateText>{negation}</NegateText>
        <ValueText value={valueStr.trim()}>{valueStr}</ValueText>
        <FeatureText>{featureStr.replace("| Blood", "")}</FeatureText>
        {featureStr.indexOf("| Blood") !== -1 && (
          <BloodText>{"(Blood)"}</BloodText>
        )}
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.EXPOSURE_TO &&
    !relationsToIgnore.includes(Relations.EXPOSURE_TO)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ValueText>{valueStr}</ValueText>
        <BloodText>
          {" ("}
          <NegateText>{negation}</NegateText>
          {"Exposure)"}
        </BloodText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.OCCUPATION &&
    !relationsToIgnore.includes(Relations.OCCUPATION)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ValueText>{valueStr}</ValueText>
        <BloodText>
          {" ("}
          <NegateText>{negation}</NegateText>
          {"Occupation)"}
        </BloodText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.ETHNIC_GROUP &&
    !relationsToIgnore.includes(Relations.ETHNIC_GROUP)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ValueText>{valueStr}</ValueText>
        <BloodText>
          {" ("}
          <NegateText>{negation}</NegateText>
          {"Ethnic Group)"}
        </BloodText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.TRAVEL_HISTORY &&
    !relationsToIgnore.includes(Relations.TRAVEL_HISTORY)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ValueText>{valueStr}</ValueText>
        <BloodText>
          {" ("}
          <NegateText>{negation}</NegateText>
          {"Travel History)"}
        </BloodText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.GEOGRAPHIC &&
    !relationsToIgnore.includes(Relations.GEOGRAPHIC)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ValueText>{valueStr}</ValueText>
        <BloodText>
          {" ("}
          <NegateText>{negation}</NegateText>
          {"Location)"}
        </BloodText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.FAMILY_HISTORY &&
    !relationsToIgnore.includes(Relations.FAMILY_HISTORY)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <NegateText>{negation}</NegateText>
        <ValueText>{valueStr}</ValueText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.PROCEDURE_FINDING &&
    !relationsToIgnore.includes(Relations.PROCEDURE_FINDING)
  ) {
    return (
      <React.Fragment key={feature.id}>
        <ResultText positive={(!negate).toString()}>
          {negate ? "Negative " : "Positive "}
        </ResultText>
        <ValueText>{valueStr}</ValueText>
      </React.Fragment>
    );
  } else if (
    value?.relation?.id === Relations.GENDER &&
    !relationsToIgnore.includes(Relations.GENDER)
  ) {
    return (
      <FeatureText key={feature.id}>
        {value.value.id === GenderValue.MALE ? "Male" : "Female"}
      </FeatureText>
    );
  } else if (feature?.id && isPopulationFeature(feature)) {
    return (
      <React.Fragment key={feature?.id}>
        <NegateText>{negate ? "Not " : ""}</NegateText>
        <FeatureText>{valueStr}</FeatureText>
      </React.Fragment>
    );
  } else if (feature["category"]?.id === KahunCategories.LAB_TEST) {
    return (
      <React.Fragment key={feature.id}>
        <ResultText positive={(!negate).toString()}>
          {negate ? "Negative " : "Positive "}
        </ResultText>
        <FeatureText>{featureStr}</FeatureText>
      </React.Fragment>
    );
  } else {
    if (feature) {
      if (featuresToOmit.includes(feature.id)) {
        return (
          <React.Fragment key={feature.id}>
            <NegateText>{negation}</NegateText>
            <ValueText>
              {value ? " " : ""}
              {valueStr}
            </ValueText>
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment key={feature.id}>
            <NegateText>{negation}</NegateText>
            <FeatureText>{featureStr}</FeatureText>
            <ValueText>
              {value ? " " : ""}
              {valueStr}
            </ValueText>
          </React.Fragment>
        );
      }
    } else {
      return "";
    }
  }
}
